<template>
  <Dialog
    id="messageComposeDialog"
    modal
    :visible="visible"
    @update:visible="close()"
    style="min-width: 320px; max-width: 720px; width: 90%"
    :closable="true"
  >
    <template #header>
      <h3>Neue Nachricht</h3>
    </template>

    <div class="compose-field">
      <label for="messageRecipients">Empfänger</label>
      <MultiSelect
        id="messageRecipients"
        class="recipient-select"
        v-model="selectedKeys"
        :options="groupedRecipients"
        optionGroupLabel="group"
        optionGroupChildren="items"
        optionLabel="label"
        optionValue="key"
        :filter="true"
        :showToggleAll="false"
        :virtualScrollerOptions="recipientScrollerOptions"
        placeholder="Empfänger auswählen"
        panelClass="recipient-panel"
        style="width: 100%"
      >
        <template #value>
          <span v-if="selectedRecipientCount == 0" class="value-placeholder">
            Empfänger auswählen
          </span>
          <template v-else-if="selectedRecipientCount <= maxShownRecipients">
            <span v-for="recipient of selectedRecipients" :key="recipient.key" class="value-chip">
              {{ recipient.label }}
              <i class="pi pi-times-circle" @click.stop="removeRecipient(recipient)" />
            </span>
          </template>
          <span v-else>{{ selectedRecipientCount }} Empfänger</span>
        </template>

        <template #optiongroup="slotProps">
          <div class="group-header">
            <Checkbox
              v-if="slotProps.option.selectable"
              :binary="true"
              :modelValue="isGroupSelected(slotProps.option)"
              @update:modelValue="(selected) => toggleGroup(slotProps.option, selected)"
              @click.stop
            />
            <span>{{ slotProps.option.group }}</span>
          </div>
        </template>
      </MultiSelect>
    </div>

    <div class="compose-count">
      {{ selectedRecipientCount }} Empfänger ausgewählt
      <span v-if="selectedRecipientCount > 1">
        &mdash; jeder Empfänger erhält eine eigene Nachricht und antwortet nur an dich.
      </span>
    </div>

    <div class="compose-field">
      <label for="messageSubject">Betreff</label>
      <InputText
        id="messageSubject"
        v-model="subject"
        :maxlength="maxSubjectLength"
        style="width: 100%"
      />
    </div>

    <div class="compose-field">
      <label for="messageText">Nachricht</label>
      <Textarea
        id="messageText"
        v-model="message"
        :autoResize="false"
        :maxlength="maxMessageLength"
        style="width: 100%; height: 180px"
      />
      <span class="compose-counter">{{ message.length }} / {{ maxMessageLength }}</span>
    </div>

    <div v-if="errorMessage" class="compose-error">{{ errorMessage }}</div>

    <template #footer>
      <Button
        label="Senden"
        icon="pi pi-send"
        class="p-button-text"
        :disabled="!sendAllowed"
        @click="send()"
      />
      <Button label="Abbrechen" icon="pi pi-times" @click="close()" />
    </template>
  </Dialog>
</template>

<script>
import ServerConnection from "../../services/ServerConnection/ServerConnection";
import { reportFailure } from "../../services/ServerConnection/FailureToast";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import Textarea from "primevue/textarea";

const MAX_MESSAGE_LENGTH = 1000;
const MAX_SUBJECT_LENGTH = 200;
const ALL_LEAGUES_GROUP = "Alle Ligen";
const MAX_SHOWN_RECIPIENTS = 10;
const RECIPIENT_ITEM_SIZE = 38;

export default {
  name: "MessageComposeDialog",
  components: {
    Button,
    Checkbox,
    Dialog,
    InputText,
    MultiSelect,
    Textarea,
  },
  emits: ["sent", "closed"],
  props: {
    visible: Boolean,
  },
  data() {
    return {
      recipients: [],
      selectedKeys: [],
      subject: "",
      message: "",
      sending: false,
      errorMessage: null,
      maxMessageLength: MAX_MESSAGE_LENGTH,
      maxSubjectLength: MAX_SUBJECT_LENGTH,
      maxShownRecipients: MAX_SHOWN_RECIPIENTS,
      recipientScrollerOptions: { itemSize: RECIPIENT_ITEM_SIZE },
    };
  },
  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
        if (isVisible) {
          this.resetDialog();
          this.loadRecipients();
        }
      },
    },
  },
  computed: {
    groupedRecipients() {
      var groups = [];
      this.recipients.forEach((recipient) => {
        var group = groups.find((candidate) => candidate.group == recipient.group);
        if (!group) {
          group = { group: recipient.group, items: [] };
          groups.push(group);
        }
        group.items.push(recipient);
      });
      groups.forEach((group) => {
        group.selectable = group.items.every((item) => item.selectableByLeague);
      });
      var firstLeagueGroup = groups.findIndex((group) => group.selectable);
      if (groups.filter((group) => group.selectable).length > 1) {
        groups.splice(firstLeagueGroup, 0, {
          group: ALL_LEAGUES_GROUP,
          items: [],
          selectable: true,
          allLeagues: true,
        });
      }
      return groups;
    },
    selectedKeySet() {
      return new Set(this.selectedKeys);
    },
    keysByGroup() {
      var keys = {};
      this.groupedRecipients.forEach((group) => {
        keys[group.group] = group.items.map((item) => item.key);
      });
      keys[ALL_LEAGUES_GROUP] = this.groupedRecipients
        .filter((group) => group.selectable && !group.allLeagues)
        .flatMap((group) => group.items.map((item) => item.key));
      return keys;
    },
    selectedRecipients() {
      var selected = this.selectedKeySet;
      var seen = new Set();
      return this.recipients.filter((recipient) => {
        if (!selected.has(recipient.key)) {
          return false;
        }
        var identity = recipient.type + ":" + recipient.id;
        if (seen.has(identity)) {
          return false;
        }
        seen.add(identity);
        return true;
      });
    },
    selectedRecipientCount() {
      return this.selectedRecipients.length;
    },
    sendAllowed() {
      return (
        !this.sending &&
        this.selectedKeys.length > 0 &&
        this.subject.trim().length > 0 &&
        this.message.trim().length > 0
      );
    },
  },
  methods: {
    resetDialog() {
      this.selectedKeys = [];
      this.subject = "";
      this.message = "";
      this.errorMessage = null;
      this.sending = false;
    },
    loadRecipients() {
      ServerConnection.get("messages/recipients")
        .then((response) => {
          this.recipients = response.data.map((recipient) => ({
            ...recipient,
            key: recipient.group + ":" + recipient.type + ":" + recipient.id,
          }));
        })
        .catch((error) => reportFailure(this, error, "Empfänger konnten nicht geladen werden"));
    },
    addKeys(keys) {
      var merged = new Set(this.selectedKeys);
      keys.forEach((key) => merged.add(key));
      this.selectedKeys = Array.from(merged);
    },
    removeKeys(keys) {
      var removed = new Set(keys);
      this.selectedKeys = this.selectedKeys.filter((key) => !removed.has(key));
    },
    keysOfGroup(group) {
      return this.keysByGroup[group.allLeagues ? ALL_LEAGUES_GROUP : group.group] || [];
    },
    isGroupSelected(group) {
      var keys = this.keysOfGroup(group);
      var selected = this.selectedKeySet;
      return keys.length > 0 && keys.every((key) => selected.has(key));
    },
    toggleGroup(group, selected) {
      var keys = this.keysOfGroup(group);
      if (selected) {
        this.addKeys(keys);
        return;
      }
      this.removeKeys(keys);
    },
    removeRecipient(recipient) {
      this.removeKeys(
        this.recipients
          .filter((candidate) => candidate.type == recipient.type && candidate.id == recipient.id)
          .map((candidate) => candidate.key)
      );
    },
    send() {
      if (!this.sendAllowed) {
        return;
      }
      this.sending = true;
      this.errorMessage = null;
      ServerConnection.post("messages", {
        subject: this.subject.trim(),
        message: this.message.trim(),
        recipients: this.selectedRecipients.map((recipient) => ({
          type: recipient.type,
          id: recipient.id,
        })),
      })
        .then((response) => {
          this.sending = false;
          this.$emit("sent", response.data ? response.data.createdConversations : 0);
        })
        .catch((error) => {
          this.sending = false;
          if (error.response && error.response.status == 400) {
            this.errorMessage = error.response.data;
            return;
          }
          reportFailure(this, error, "Nachricht konnte nicht gesendet werden");
        });
    },
    close() {
      this.$emit("closed");
    },
  },
};
</script>

<style>
.recipient-panel .p-multiselect-item {
  padding-left: 2.5rem;
}
.recipient-panel .p-multiselect-item-group {
  padding-left: 0.75rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
</style>

<style scoped>
.recipient-select :deep(.p-multiselect-label) {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  white-space: normal;
}
.value-placeholder {
  color: #6c757d;
}
.value-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.25rem 0.5rem;
  border-radius: 16px;
  background: #dee2e6;
  color: #495057;
}
.value-chip .pi-times-circle {
  cursor: pointer;
}
.compose-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 10px 0px;
}
.group-header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.compose-count {
  font-size: 13px;
  color: #555;
  margin-bottom: 6px;
}
.compose-counter {
  font-size: 12px;
  color: #777;
  align-self: flex-end;
}
.compose-error {
  color: #b00020;
  font-size: 13px;
  margin-top: 6px;
}
</style>
